import { getMetadata } from 'valibot'

import type {
  ClientAddressLookupResult,
  CSVRow,
  MilitaryTime,
} from '@fv/client-types'
import type { AddressType, LocationAccessorialDTO } from '@fv/models'
import {
  type AddressUpload,
  type AddressUploadMeta,
  addressUploadSchema,
} from '@fv/models/address-upload'
import { type IValidationErrorEntry, validateSync } from '@fv/models/validation'

import type { FormAddress } from './types'

export type AddressBookUploadRecord = Partial<AddressUpload>

type CsvColumnMatcher = {
  key: keyof AddressBookUploadRecord
  pattern: RegExp
  parse?: (value: string) => unknown
}

const csvColumnMatchers = Object.entries(addressUploadSchema.entries).reduce<
  CsvColumnMatcher[]
>((acc, [key, schema]) => {
  const metadata: AddressUploadMeta = getMetadata(schema)

  if (metadata?.csvMatch) {
    acc.push({
      key: key as keyof AddressBookUploadRecord,
      pattern: metadata.csvMatch,
      parse: metadata.parse,
    })
  }
  return acc
}, [])

const getMappedCol = (colName: string): CsvColumnMatcher | undefined => {
  return csvColumnMatchers.find(m => m.pattern.test(colName))
}

export const parseAddressCsv = (data: CSVRow[]): AddressBookUploadRecord[] => {
  if (!data.length) return []
  const colMap = Object.keys(data[0]).map(k => ({
    original: k,
    matcher: getMappedCol(k),
  }))

  return data.map(d =>
    Object.fromEntries(
      Object.entries(d)
        .map(([key, value]) => {
          const matcher = colMap.find(c => c.original === key)?.matcher
          if (!matcher) return undefined

          let parsed: unknown = value
          if (matcher.parse) {
            parsed = matcher.parse(value)
          }

          return [matcher.key, parsed]
        })
        .filter(Boolean),
    ),
  )
}

const validateRow = (
  row: AddressBookUploadRecord,
  type: AddressType,
): IValidationErrorEntry[] => {
  const errors: IValidationErrorEntry[] = []
  const { errors: schemaErrors } = validateSync(addressUploadSchema, row)
  for (const e of schemaErrors) {
    if (!errors.some(existing => existing.propertyName === e.propertyName)) {
      errors.push(e)
    }
  }

  if (type !== 'customs-broker' && !row.address?.trim()) {
    errors.push({ propertyName: 'address', message: 'Required' })
  }

  if (type === 'billing') {
    if (!row.contactName?.trim()) {
      errors.push({ propertyName: 'contactName', message: 'Required' })
    }
    if (!row.contactPhone?.trim()) {
      errors.push({ propertyName: 'contactPhone', message: 'Required' })
    }
    if (!row.city?.trim()) {
      errors.push({ propertyName: 'city', message: 'Required' })
    }
    if (!row.postalCode?.trim()) {
      errors.push({ propertyName: 'postalCode', message: 'Required' })
    }
  }

  return errors
}

export type RowErrorMap = Map<AddressBookUploadRecord, Set<string>>

export const buildRowErrorMap = (
  data: AddressBookUploadRecord[],
  type: AddressType,
  invalidPostalCodes?: Set<string>,
): RowErrorMap => {
  const map: RowErrorMap = new Map()
  for (const row of data) {
    if (!rowHasData(row)) continue
    const errors = validateRow(row, type)
    const errorCols = new Set(errors.map(e => e.propertyName))
    if (
      !errorCols.has('postalCode') &&
      isPostalCodeInvalid(row.postalCode, invalidPostalCodes)
    ) {
      errorCols.add('postalCode')
    }
    if (errorCols.size > 0) {
      map.set(row, errorCols)
    }
  }
  return map
}

export const isPostalCodeInvalid = (
  postalCode: string | undefined,
  invalidPostalCodes: Set<string> | undefined,
): boolean => {
  if (!invalidPostalCodes || !postalCode) return false
  return invalidPostalCodes.has(postalCode.trim().toUpperCase())
}

export const getValidationErrors = (
  data: AddressBookUploadRecord[],
  type: AddressType,
  invalidPostalCodes?: Set<string>,
) => {
  return data.flatMap((row, i) => {
    if (!rowHasData(row)) return []

    const errors = validateRow(row, type).map(e => ({
      row: i,
      column: e.propertyName,
      message: e.message,
    }))

    if (
      !errors.some(e => e.column === 'postalCode') &&
      isPostalCodeInvalid(row.postalCode, invalidPostalCodes)
    ) {
      errors.push({
        row: i,
        column: 'postalCode',
        message: 'Invalid postal code',
      })
    }

    return errors
  })
}

export const rowHasData = ({
  address,
  city,
  state,
  postalCode,
  company,
  shipType,
}: AddressBookUploadRecord): boolean => {
  return !!(address || city || state || postalCode || company || shipType)
}

export const buildCreateModel = (row: AddressBookUploadRecord): FormAddress => {
  const ltlAccessorials: LocationAccessorialDTO[] = []
  const parcelAccessorials: LocationAccessorialDTO[] = []
  const truckloadAccessorials: LocationAccessorialDTO[] = []

  if (row.adultSignatureRequired) {
    parcelAccessorials.push({ key: 'adult-signature-required' })
  }
  if (row.signatureRequired) {
    parcelAccessorials.push({ key: 'signature-required' })
  }
  if (row.indirectSignature) {
    parcelAccessorials.push({ key: 'indirect-signature-required' })
  }
  if (row.liftgateDelivery || row.liftgatePickup) {
    ltlAccessorials.push({ key: 'liftgate' })
  }
  if (row.insidePickup || row.insideDelivery) {
    ltlAccessorials.push({ key: 'inside' })
  }
  if (row.callNotifyBeforeDelivery) {
    ltlAccessorials.push({ key: 'notify' })
  }
  if (row.deliveryAppointment) {
    ltlAccessorials.push({ key: 'schedule' })
  }
  if (row.sortAndSegregate) {
    ltlAccessorials.push({ key: 'sort-and-segregate' })
  }
  if (row.twic) {
    truckloadAccessorials.push({ key: 'twic' })
  }
  return {
    address: row.address ?? '',
    address2: row.address2 ?? '',
    city: row.city ?? '',
    state: row.state ?? '',
    company: row.company ?? '',
    postalCode: row.postalCode ?? '',
    instructions: row.specialInstructions ?? '',
    internalNotes: row.internalNotes ?? '',
    contactEmail: row.contactEmail ?? '',
    contactName: row.contactName ?? '',
    contactPhone: row.contactPhone ?? '',
    contactPhoneExt: row.contactPhoneExt ?? '',
    country: row.country || 'us',
    isAccountLocation: false,
    closesAt: (row.dockHoursEnd as MilitaryTime) || undefined,
    opensAt: (row.dockHoursStart as MilitaryTime) || undefined,

    mappedAccessorials: {
      ltl: ltlAccessorials,
      parcel: parcelAccessorials,
      truckload: truckloadAccessorials,
    },
    bolFollowers: row.bolFollowers ?? '',
    sendBOL: row.sendBol ?? false,
    shipType: row.shipType ?? '',
    ...(row.brokerType && {
      customsBroker: {
        type: row.brokerType,
      },
    }),
    ...(row.flatbedHandlingType && {
      flatbedHandlingType: row.flatbedHandlingType,
    }),
  }
}

export const normalizeAddressResult = (address: ClientAddressLookupResult) => {
  return {
    score: 0, // add the score to ensure it's sorted - let it be overridden by the score from the search
    ...address,
  }
}

export const customsBrokerTemplate = `Company*,Address,City,State,Postal Code,Address 2,Contact name,Contact phone*,Contact Phone Ext,Contact email,Country,Broker Type
My Company,901 Carondelet Dr,Kansas City,MO,64114,,,5555555555,x1234,,us,import
Another Company,,,,,,,,,,us,export
`

export const billingTemplate = `Company*,Address*,City*,State*,Postal Code*,Address 2,Contact name*,Contact phone*,Contact Phone Ext,Contact email,Country
My Company,901 Carondelet Dr,Kansas City,MO,64114,,John Doe,5555555555,x1234,,us
Another Company,901 Carondelet Dr,Kansas City,MO,64114,,Jane Doe,5555555556,,,`

export const addressBookTemplate = `Company*,Address*,City*,State*,Postal Code*,Address 2,Contact name,Contact phone,Contact Phone Ext,Contact email,Country,Location type*,Share with,Special instructions,Internal notes,Dock hours start,Dock hours end,Liftgate pickup,Liftgate delivery,Inside pickup,Inside delivery,Call/notify before delivery,Delivery appointment,Sort and segregate,No signature required,Signature required,Adult signature required,Indirect signature,TWIC required,Send Bol,Flatbed Handling Type
My Company,901 Carondelet Dr,Kansas City,MO,64114,,,5555555555,x1234,,,business dock,share@example.com,,,8:00AM,5:00PM,x,x,x,x,x,x,x,x,x,x,x,x,x,dock-height
Another Company,901 Carondelet Dr,Kansas City,MO,64114,,,,,,,business no dock,,,,,,,,,,,,,,,,,,,`
